import React, { FC, useEffect } from 'react'
import { Alert, Form, Input, notification } from 'antd'
import { FormInstance } from 'antd/es/form/hooks/useForm'

import '../index.less'
import { MEDISPRING_INDENTIFIER, useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation } from '../../../../core/api/interopApi'
import { SpinLoader } from '../../../common/SpinLoader'
import { useGetAndTryDecryptMainAttachmentAsJsonQuery } from '../../../../core/api/documentApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetPractitionerQuery } from '../../../../core/api/practitionerApi'
import { findPropertyValue } from '../../../../helpers/property'

interface MedispringConnectionProps {
  form: FormInstance
  onClose: () => void
  practitionerId?: string
  t: (key: string, ctxt?: { [key: string]: string }) => string
}

export const MedispringConnection: FC<MedispringConnectionProps> = ({ form, onClose, practitionerId, t }) => {
  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(practitionerId ?? skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const [giveAccessToNewPrivateKey, { isSuccess: isAccessGivenSuccessfully, isLoading: isGiveAccessToNewPrivateKeyLoading }] =
    useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation()
  const existingMedispringDocumentId: string | undefined = findPropertyValue(practitioner?.properties, MEDISPRING_INDENTIFIER)
  const { data: decryptedMedispringDocument, isFetching: isDecryptedMedispringDocumentLoading } = useGetAndTryDecryptMainAttachmentAsJsonQuery(
    existingMedispringDocumentId ?? skipToken,
    {
      refetchOnMountOrArgChange: true, // re-fetch even if cached
      refetchOnFocus: true, // optional: also refetch on window focus
      refetchOnReconnect: true, // optional: refetch after reconnect
    },
  )

  useEffect(() => {
    if (isAccessGivenSuccessfully) {
      onClose()
    }
  }, [isAccessGivenSuccessfully])

  const notify = (text: string, type: 'success' | 'error' = 'success') => {
    notification[type]({
      type: type,
      message: text,
      duration: 30,
    })
  }

  // Modify the MS database with RetinoBridge data
  // Download the user's private key from the Cardinal SDK and trigger a file download
  const handleSubmit = async (values: { msLogin: string; msPassword: string }) => {
    const { msLogin, msPassword } = values

    giveAccessToNewPrivateKey({ username: msLogin, password: msPassword })
      .unwrap()
      .then(() => {
        notify(t('modalManageAccountForm.medispring.notifications.success'))
      })
      .catch((error: Error & { error: string | undefined }) => {
        console.error('Failed to add private key:', error)
        notify(error.message ?? error.error ?? t('modalManageAccountForm.medispring.notifications.error'), 'error')
      })
  }

  const isLoading = isGiveAccessToNewPrivateKeyLoading || isDecryptedMedispringDocumentLoading || isPractitionerFetching

  if (isLoading) {
    return (
      <div className="modalManageAccountForm__form">
        <SpinLoader />
      </div>
    )
  }

  return (
    <>
      <Form
        className="modalManageAccountForm__form"
        layout="vertical"
        onFinish={async (values) => {
          await handleSubmit(values)
        }}
        colon={false}
        form={form}
        initialValues={{
          msLogin: decryptedMedispringDocument?.username,
          msPassword: decryptedMedispringDocument?.password,
        }}
      >
        <Alert message={t('modalManageAccountForm.medispring.alert')} type="info" showIcon={true} />
        <div className="modalManageAccountForm__form__inputs">
          <Form.Item
            name="msLogin"
            label={t('modalManageAccountForm.medispring.loginLabel')}
            rules={[{ required: true, message: t('modalManageAccountForm.medispring.loginIsRequired') }]}
          >
            <Input placeholder={t('modalManageAccountForm.medispring.loginLabel')} size="large" />
          </Form.Item>
          <Form.Item
            name="msPassword"
            label={t('modalManageAccountForm.medispring.password')}
            rules={[{ required: true, message: t('modalManageAccountForm.medispring.passwordIsRequired') }]}
          >
            <Input type="password" placeholder={t('modalManageAccountForm.medispring.password')} size="large" />
          </Form.Item>
        </div>
      </Form>
    </>
  )
}
